// Extend the global Window interface to include the dataLayer property used by GTM/mParticle
declare global {
    interface Window {
        dataLayer: any[];
    }
}

// Payload structure expected by the product click tracking function
type ProductTrackingPayload = {
    text: string; // The label of the clicked element ("View More")
    product: {
        name: string; // Product name
        url: string; // Product detail URL
        image?: string; // Optional product image URL
    };
};

/**
 * Pushes a custom event into the window.dataLayer.
 * Used by GTM or other tag managers for analytics tracking.
 */
export function trackEvent(event: Record<string, unknown> & { event: string }) {
    if (typeof window === 'undefined') return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(event);
}

/**
 * Tracks a single product click event.
 * Pushes a structured 'view_product' event with standard fields to the dataLayer.
 */
export function trackProductClick(payload: ProductTrackingPayload) {
    trackEvent({
        event: 'view_product',
        click_text: payload.text,
        click_url: payload.product.url,
        current_page: window.location.href,
        product_name: payload.product.name,
        product_image: payload.product.image,
    });
}

/**
 * Hashes an email using SHA-256 and returns a hex string.
 */
export async function hashEmail(email: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(email.trim().toLowerCase());
    const buffer = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(buffer))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
}

/**
 * Pushes userIdentities into dataLayer after hashing the email.
 */
export async function trackUserIdentities(email: string): Promise<void> {
    if (typeof window === 'undefined') return;

    const hashedEmail = await hashEmail(email);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        userIdentities: {
            email,
            other: hashedEmail,
        },
    });
}
